
import React, { useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchExamReport, fetchSectionExamReports } from "../../../redux/examSlice";
import * as XLSX from "xlsx";
import { 
  generatePDFFromElement, 
  generateSectionReportPDF, 
  generateStudentReportPDF 
} from "../../../../src/utils/invoiceUtils";

const ViewReportModal = ({ 
  isOpen, 
  onClose, 
  studentId, 
  sectionId, 
  isSectionReport, 
  sectionName, 
  className,
  campusName
}) => {
  const reportRef = useRef();
  const dispatch = useDispatch();
  const { examReport, sectionReports, loading, error } = useSelector((state) => state.exam);

  useEffect(() => {
    if (isOpen) {
      if (isSectionReport && sectionId) {
        dispatch(fetchSectionExamReports({ id: sectionId }));
      } else if (studentId) {
        dispatch(fetchExamReport(studentId));
      }
    }
  }, [isOpen, studentId, sectionId, isSectionReport, dispatch]);

  const downloadPDF = () => {
    if (isSectionReport) {
      generateSectionReportPDF(
        sectionReports,
        className,
        sectionName,
        campusName,
        "Section_Exam_Report.pdf"
      );
    } else {
      const reportData = Array.isArray(examReport) ? examReport[0] : examReport;
      if (reportData) {
        generateStudentReportPDF(reportData, campusName, "Exam_Report.pdf");
      }
    }
  };

  const downloadExcel = () => {
    if (isSectionReport) {
      if (!sectionReports || !sectionReports.length) return;
      
      const workbook = XLSX.utils.book_new();
      const summaryData = [
        ["Section Report Summary"],
        ["Campus", campusName],
        ["Class", className],
        ["Section", sectionName],
        [],
        ["Student Name", "Overall Percentage", "Overall Grade"],
      ];
      
      sectionReports.forEach(report => {
        summaryData.push([
          report.studentName,
          report.overallPercentage + "%",
          report.overallGrade
        ]);
      });
      
      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");
      sectionReports.forEach(report => {
        const studentData = [
          ["Student Name", report.studentName],
          ["Campus", campusName],
          ["Class", report.className],
          ["Section", report.sectionName],
          [],
          ["Subject", "Total Marks", "Obtained Marks", "Percentage", "Grade"],
        ];
        
        if (report.subjects && Array.isArray(report.subjects)) {
          studentData.push(
            ...report.subjects.map((subject) => [
              subject.subjectName,
              subject.totalMarks,
              subject.obtainedMarks,
              subject.percentage,
              subject.grade,
            ])
          );
        }
        
        studentData.push(["Overall", "", "", report.overallPercentage, report.overallGrade]);
        
        const studentSheet = XLSX.utils.aoa_to_sheet(studentData);
        XLSX.utils.book_append_sheet(workbook, studentSheet, report.studentName.substring(0, 31));
      });
      
      XLSX.writeFile(workbook, "Section_Exam_Report.xlsx");
    } else {
      if (!examReport) return;

      const report = Array.isArray(examReport) ? examReport[0] : examReport;
      if (!report) return;

      const worksheetData = [
        ["Student Name", report.studentName],
        ["Campus", campusName],
        ["Class", report.className],
        ["Section", report.sectionName],
        [],
        ["Subject", "Total Marks", "Obtained Marks", "Percentage", "Grade"],
      ];
      if (report.subjects && Array.isArray(report.subjects)) {
        worksheetData.push(
          ...report.subjects.map((subject) => [
            subject.subjectName,
            subject.totalMarks,
            subject.obtainedMarks,
            subject.percentage,
            subject.grade,
          ])
        );
      }

      worksheetData.push(["Overall", "", "", report.overallPercentage, report.overallGrade]);

      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Exam Report");
      XLSX.writeFile(workbook, "Exam_Report.xlsx");
    }
  };

  if (!isOpen) return null;

  const reportData = !isSectionReport && examReport ? (Array.isArray(examReport) ? examReport[0] : examReport) : null;
  const academicYear = "2023-24"; 

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
      <div ref={reportRef} className="bg-white p-6 rounded-lg shadow-lg w-[80%] max-h-[90vh] overflow-y-auto relative border border-gray-300">
        {loading && <p>Loading report...</p>}
        {error && <p className="text-red-500">Error: {error}</p>}

        {isSectionReport ? (
          <>
            {sectionReports && sectionReports.length > 0 ? (
              sectionReports.map((reportData, index) => (
                <div key={index} style={{ pageBreakAfter: index < sectionReports.length - 1 ? 'always' : 'auto' }}>
                  {/* Individual Student Report in Kingston College Format */}
                  <div className="max-w-4xl mx-auto">
                    {/* Header */}
                    <div className="text-center mb-6">
                      <h1 className="text-xl font-bold text-gray-800">{campusName}</h1>
                      <h2 className="text-lg font-semibold mt-2">End of Year Report</h2>
                      <p className="text-base">{academicYear}</p>
                      <p className="text-base font-medium">{reportData.className?.includes('-') ? 'A-Levels' : 'O-Levels'}</p>
                    </div>

                    {/* Student Info */}
                    <div className="mb-6">
                      <div className="grid grid-cols-1 gap-y-2">
                        <div className="flex">
                          <p className="w-32 font-semibold">Student Name</p>
                          <p className="flex-1 border-b border-gray-400 ml-4">{reportData.studentName}</p>
                        </div>
                        <div className="flex">
                          <p className="w-32 font-semibold">Class / Section</p>
                          <p className="w-32 border-b border-gray-400 ml-4">{reportData.className}</p>
                          <p className="w-16 font-semibold ml-8">Date</p>
                          <p className="w-32 border-b border-gray-400 ml-4">{new Date().toLocaleDateString('en-GB')}</p>
                        </div>
                      </div>
                    </div>

                    {/* Subject Table */}
                    <div className="mb-6">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr>
                            <th className="border border-gray-400 p-2 bg-gray-100 text-left">Subject</th>
                            <th className="border border-gray-400 p-2 bg-gray-100 text-center">Total Marks</th>
                            <th className="border border-gray-400 p-2 bg-gray-100 text-center">Obtained Marks</th>
                            <th className="border border-gray-400 p-2 bg-gray-100 text-center">Percentage</th>
                            <th className="border border-gray-400 p-2 bg-gray-100 text-center">Grade</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.subjects && reportData.subjects.length > 0 ? (
                            reportData.subjects.map((subject, index) => (
                              <tr key={index}>
                                <td className="border border-gray-400 p-2">{subject.subjectName}</td>
                                <td className="border border-gray-400 p-2 text-center">{subject.totalMarks}</td>
                                <td className="border border-gray-400 p-2 text-center">{subject.obtainedMarks.toFixed(2)}</td>
                                <td className="border border-gray-400 p-2 text-center">{subject.percentage.toFixed(2)}</td>
                                <td className="border border-gray-400 p-2 text-center font-semibold">{subject.grade}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="5" className="border p-2 text-center">No subjects found</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Overall Result */}
                    <div className="flex justify-center mb-6">
                      <div className="w-48 flex flex-col items-center border border-gray-400">
                        <div className="w-full text-center border-b border-gray-400 p-2 bg-gray-100 font-semibold">
                          Overall %age
                        </div>
                        <div className="w-full text-center p-2 text-lg font-semibold">
                          {reportData.overallPercentage}
                        </div>
                      </div>
                      <div className="w-48 flex flex-col items-center border border-gray-400 border-l-0">
                        <div className="w-full text-center border-b border-gray-400 p-2 bg-gray-100 font-semibold">
                          Overall Grade
                        </div>
                        <div className="w-full text-center p-2 text-lg font-semibold">
                          {reportData.overallGrade}
                        </div>
                      </div>
                    </div>

                    {/* Activity Table */}
                    <div className="mb-6">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-300">
                            <th className="border border-gray-400 p-2">Activity</th>
                            <th className="border border-gray-400 p-2">Punctuality</th>
                            <th className="border border-gray-400 p-2">Conduct</th>
                            <th className="border border-gray-400 p-2">Co-Curricular</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="border border-gray-400 p-2 bg-gray-300">GPI</td>
                            <td className="border border-gray-400 p-2 text-center">ME</td>
                            <td className="border border-gray-400 p-2 text-center">ME</td>
                            <td className="border border-gray-400 p-2 text-center">ME</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Performance Key */}
                    <div className="mb-6">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr>
                            <th colSpan={4} className="border border-gray-400 p-2 bg-gray-300 text-center">
                              General Performance Key
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="border border-gray-400 p-2 w-1/4">
                              <div className="font-semibold">SE</div>
                              <div>Significantly Exceeds Expectation</div>
                            </td>
                            <td className="border border-gray-400 p-2 w-1/4">
                              <div className="font-semibold">EE</div>
                              <div>Exceeds Expectation</div>
                            </td>
                            <td className="border border-gray-400 p-2 w-1/4">
                              <div className="font-semibold">ME</div>
                              <div>Meets Expectation</div>
                            </td>
                            <td className="border border-gray-400 p-2 w-1/4">
                              <div className="font-semibold">FE</div>
                              <div>Fails to Meet Expectations</div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Grade Key */}
                    <div className="mb-8">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-300">
                            <th className="border border-gray-400 p-2 w-1/8">Grade Key</th>
                            <th className="border border-gray-400 p-2 w-1/8">90+<br />A+</th>
                            <th className="border border-gray-400 p-2 w-1/8">80+<br />A</th>
                            <th className="border border-gray-400 p-2 w-1/8">70+<br />B</th>
                            <th className="border border-gray-400 p-2 w-1/8">60+<br />C</th>
                            <th className="border border-gray-400 p-2 w-1/8">50+<br />D</th>
                            <th className="border border-gray-400 p-2 w-1/8">40+<br />E</th>
                            <th className="border border-gray-400 p-2 w-1/8">Below 40<br />U</th>
                          </tr>
                        </thead>
                      </table>
                    </div>

                    {/* Signature Lines */}
                    <div className="grid grid-cols-3 gap-8 mt-12">
                      <div className="text-center">
                        <div className="border-t border-gray-400 mt-8 pt-2">
                          Coordinator's Signature
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="border-t border-gray-400 mt-8 pt-2">
                          Headmistress's Signature
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="border-t border-gray-400 mt-8 pt-2">
                          Principal / Vice Principal's Signature
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p>No reports found for this section</p>
            )}
          </>
        ) : reportData ? (
          <>
            {/* Individual Student Report in Kingston College Format */}
            <div className="max-w-4xl mx-auto">
              {/* Header */}
              <div className="text-center mb-6">
                <h1 className="text-xl font-bold text-gray-800">{campusName}</h1>
                <h2 className="text-lg font-semibold mt-2">End of Year Report</h2>
                <p className="text-base">{academicYear}</p>
                <p className="text-base font-medium">{reportData.className?.includes('-') ? 'A-Levels' : 'O-Levels'}</p>
              </div>

              {/* Student Info */}
              <div className="mb-6">
                <div className="grid grid-cols-1 gap-y-2">
                  <div className="flex">
                    <p className="w-32 font-semibold">Student Name</p>
                    <p className="flex-1 border-b border-gray-400 ml-4">{reportData.studentName}</p>
                  </div>
                  <div className="flex">
                    <p className="w-32 font-semibold">Class / Section</p>
                    <p className="w-32 border-b border-gray-400 ml-4">{reportData.className}</p>
                    <p className="w-16 font-semibold ml-8">Date</p>
                    <p className="w-32 border-b border-gray-400 ml-4">{new Date().toLocaleDateString('en-GB')}</p>
                  </div>
                </div>
              </div>

              {/* Subject Table */}
              <div className="mb-6">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="border border-gray-400 p-2 bg-gray-100 text-left">Subject</th>
                      <th className="border border-gray-400 p-2 bg-gray-100 text-center">Total Marks</th>
                      <th className="border border-gray-400 p-2 bg-gray-100 text-center">Obtained Marks</th>
                      <th className="border border-gray-400 p-2 bg-gray-100 text-center">Percentage</th>
                      <th className="border border-gray-400 p-2 bg-gray-100 text-center">Grade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.subjects && reportData.subjects.length > 0 ? (
                      reportData.subjects.map((subject, index) => (
                        <tr key={index}>
                          <td className="border border-gray-400 p-2">{subject.subjectName}</td>
                          <td className="border border-gray-400 p-2 text-center">{subject.totalMarks}</td>
                          <td className="border border-gray-400 p-2 text-center">{subject.obtainedMarks.toFixed(2)}</td>
                          <td className="border border-gray-400 p-2 text-center">{subject.percentage.toFixed(2)}</td>
                          <td className="border border-gray-400 p-2 text-center font-semibold">{subject.grade}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="border p-2 text-center">No subjects found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Overall Result */}
              <div className="flex justify-center mb-6">
                <div className="w-48 flex flex-col items-center border border-gray-400">
                  <div className="w-full text-center border-b border-gray-400 p-2 bg-gray-100 font-semibold">
                    Overall %age
                  </div>
                  <div className="w-full text-center p-2 text-lg font-semibold">
                    {reportData.overallPercentage}
                  </div>
                </div>
                <div className="w-48 flex flex-col items-center border border-gray-400 border-l-0">
                  <div className="w-full text-center border-b border-gray-400 p-2 bg-gray-100 font-semibold">
                    Overall Grade
                  </div>
                  <div className="w-full text-center p-2 text-lg font-semibold">
                    {reportData.overallGrade}
                  </div>
                </div>
              </div>

              {/* Activity Table */}
              <div className="mb-6">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-300">
                      <th className="border border-gray-400 p-2">Activity</th>
                      <th className="border border-gray-400 p-2">Punctuality</th>
                      <th className="border border-gray-400 p-2">Conduct</th>
                      <th className="border border-gray-400 p-2">Co-Curricular</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-400 p-2 bg-gray-300">GPI</td>
                      <td className="border border-gray-400 p-2 text-center">ME</td>
                      <td className="border border-gray-400 p-2 text-center">ME</td>
                      <td className="border border-gray-400 p-2 text-center">ME</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Performance Key */}
              <div className="mb-6">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th colSpan={4} className="border border-gray-400 p-2 bg-gray-300 text-center">
                        General Performance Key
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-400 p-2 w-1/4">
                        <div className="font-semibold">SE</div>
                        <div>Significantly Exceeds Expectation</div>
                      </td>
                      <td className="border border-gray-400 p-2 w-1/4">
                        <div className="font-semibold">EE</div>
                        <div>Ex

ceeds Expectation</div>
                      </td>
                      <td className="border border-gray-400 p-2 w-1/4">
                        <div className="font-semibold">ME</div>
                        <div>Meets Expectation</div>
                      </td>
                      <td className="border border-gray-400 p-2 w-1/4">
                        <div className="font-semibold">FE</div>
                        <div>Fails to Meet Expectations</div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Grade Key */}
              <div className="mb-8">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-300">
                      <th className="border border-gray-400 p-2 w-1/8">Grade Key</th>
                      <th className="border border-gray-400 p-2 w-1/8">90+<br />A+</th>
                      <th className="border border-gray-400 p-2 w-1/8">80+<br />A</th>
                      <th className="border border-gray-400 p-2 w-1/8">70+<br />B</th>
                      <th className="border border-gray-400 p-2 w-1/8">60+<br />C</th>
                      <th className="border border-gray-400 p-2 w-1/8">50+<br />D</th>
                      <th className="border border-gray-400 p-2 w-1/8">40+<br />E</th>
                      <th className="border border-gray-400 p-2 w-1/8">Below 40<br />U</th>
                    </tr>
                  </thead>
                </table>
              </div>

              {/* Signature Lines */}
              <div className="grid grid-cols-3 gap-8 mt-12">
                <div className="text-center">
                  <div className="border-t border-gray-400 mt-8 pt-2">
                    Coordinator's Signature
                  </div>
                </div>
                <div className="text-center">
                  <div className="border-t border-gray-400 mt-8 pt-2">
                    Headmistress's Signature
                  </div>
                </div>
                <div className="text-center">
                  <div className="border-t border-gray-400 mt-8 pt-2">
                    Principal / Vice Principal's Signature
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : null}

        {/* Download Buttons */}
        <div className="no-print flex justify-end mt-6 space-x-4">
          <button onClick={downloadPDF} className="bg-blue-500 px-4 py-2 rounded text-white">
            Download as PDF
          </button>
          <button onClick={downloadExcel} className="bg-green-500 px-4 py-2 rounded text-white">
            Download as Excel
          </button>
          <button onClick={onClose} className="bg-gray-400 px-4 py-2 rounded text-white">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewReportModal;